import type {ValueType} from '@github-ui/custom-properties-types'
import {getEmptyValueByType} from '@github-ui/custom-properties-types/helpers'
import {validateRegexPattern} from '@github-ui/repos-async-validation/validate-regex-pattern'

import type {DefinitionFormSchema, DefinitionValidator} from './definition-form-types'
import {getDefinitionLabel} from './definition-type-label'
import {
  validateAllowedValue,
  validatePropertyDefaultValue,
  validatePropertyDescription,
  validatePropertyNameSync,
} from './validate'

export const NEW_ALLOWED_VALUE_ELEMENT_ID = 'option-input'
export const PROPERTY_TYPE_DROPDOWN_BUTTON_ID = 'type-dropdown-button'

export const validatePropertyName: DefinitionValidator<DefinitionFormSchema> = async (data, _errors, ctx) => {
  const {propertyName} = data
  const {existingPropertyNames, formMode} = ctx

  if (formMode === 'update') return

  const error = validatePropertyNameSync(propertyName, existingPropertyNames)

  if (error) {
    return {fieldErrors: {propertyName: [error]}}
  }
}

export const validateDescription: DefinitionValidator<DefinitionFormSchema> = async data => {
  const {description} = data

  const error = validatePropertyDescription(description)

  if (error) {
    return {fieldErrors: {description: [error]}}
  }
}

export function supportsAllowedValues(valueType: ValueType) {
  return valueType === 'single_select' || valueType === 'multi_select'
}

export const validateNewAllowedValue: DefinitionValidator<DefinitionFormSchema> = async (data, _errors, ctx) => {
  const {newAllowedValue, allowedValues, valueType} = data

  if (!supportsAllowedValues(valueType)) {
    return
  }

  const error = validateAllowedValue(newAllowedValue || '', new Set(allowedValues), ctx.propertiesLimits)

  if (error) {
    return {
      fieldErrors: {
        newAllowedValue: [error],
      },
    }
  }
}

export const validateAllowedValuesList: DefinitionValidator<DefinitionFormSchema> = async data => {
  const {allowedValues, valueType} = data

  if (!supportsAllowedValues(valueType)) {
    return
  }

  if (allowedValues.length === 0) {
    const typeLabel = getDefinitionLabel(valueType)
    const error = `Property of type ${typeLabel} must have at least one option. Please either <a href="#${NEW_ALLOWED_VALUE_ELEMENT_ID}">add an option</a> or <a href="#${PROPERTY_TYPE_DROPDOWN_BUTTON_ID}">change the property type</a>.`

    // Set as form error.
    return {formErrors: [error]}
  }
}

export const validateDefaultValue: DefinitionValidator<DefinitionFormSchema> = async (data, errors, ctx) => {
  const {required, defaultValue, valueType, allowedValues, regexEnabled, regexPattern} = data

  if (!required) return

  const hasExistingPatternErrors = !!errors.fieldErrors['regexPattern']?.length

  if (regexEnabled && hasExistingPatternErrors) return

  const error = await validatePropertyDefaultValue(
    defaultValue || getEmptyValueByType(valueType),
    valueType,
    required,
    allowedValues,
    // Skip validation if regex pattern is invalid
    hasExistingPatternErrors ? undefined : regexPattern,
    ctx.propertiesLimits,
  )
  if (error) {
    return {
      fieldErrors: {
        defaultValue: [error],
      },
    }
  }
}

function supportsRegex(valueType: ValueType) {
  return valueType === 'string'
}

export const validateRegex: DefinitionValidator<DefinitionFormSchema> = async data => {
  const {regexEnabled, regexPattern, valueType} = data

  if (!supportsRegex(valueType)) return
  if (!regexEnabled) return

  if (!regexPattern) {
    return {
      fieldErrors: {
        regexPattern: ['Regular expression pattern is required'],
      },
    }
  }

  if (!(await validateRegexPattern(regexPattern))) {
    return {
      fieldErrors: {
        regexPattern: ['Invalid pattern'],
      },
    }
  }
}
